import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy, primeroDelMes } from '../utils/formato';

// Nómina: la gente que cobra por periodo.
//
// Aquí va solo el que se paga por tiempo —diario, semanal, quincenal o
// mensual—, que es un costo fijo: se paga aunque no se venda nada.
//
// El instalador que cobra por obra NO se paga aquí: su pago se anota en la
// venta, como costo de ese trabajo. Si se llevara junto con la nómina, la
// instalación aparecería sin costo y se vería dejando el 100%.
//
// Cada pago guarda el periodo que cubre. Sin «desde» y «hasta» no hay forma de
// saber si a alguien se le pagó dos veces la misma quincena, que es el error
// que de verdad ocurre cuando se paga en efectivo y con prisa.

const PERIODOS = [
  { id: 'diario',     nombre: 'Diario',     dias: 1 },
  { id: 'semanal',    nombre: 'Semanal',    dias: 7 },
  { id: 'quincenal',  nombre: 'Quincenal',  dias: 15 },
  { id: 'mensual',    nombre: 'Mensual',    dias: 30 },
  { id: 'por_trabajo',nombre: 'Por trabajo', dias: 0 },
];

const VACIO = { nombre: '', documento: '', telefono: '', cargo: '',
                periodicidad: 'quincenal', sueldo: '', nota: '' };

const sumarDias = (f, n) => {
  const d = new Date(f + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

function Nomina({ onCambio }) {
  const [trabajadores, setTrabajadores] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoy());

  const [editando, setEditando] = useState(null);
  const [pagando, setPagando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/trabajadores').then(r => setTrabajadores(r.data || [])).catch(() => {});
    axios.get('/api/pagos-trabajadores').then(r => setPagos(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const enRango = pagos.filter(p => p.fecha >= desde && p.fecha <= hasta);
  const totalPagado = enRango.reduce((s, p) => s + (p.monto || 0), 0);

  const porPeriodo = trabajadores.filter(t => t.periodicidad !== 'por_trabajo');
  const porObra = trabajadores.filter(t => t.periodicidad === 'por_trabajo');

  // Cuánto cuesta al mes tener el negocio abierto, en gente.
  const costoMensual = porPeriodo.reduce((s, t) => {
    const p = PERIODOS.find(x => x.id === t.periodicidad);
    if (!p || !p.dias) return s;
    return s + (t.sueldo || 0) * (30 / p.dias);
  }, 0);

  const ultimoPago = (id) =>
    pagos.filter(p => p.trabajador_id === id)
         .sort((a, b) => (b.hasta || b.fecha).localeCompare(a.hasta || a.fecha))[0];

  const abrirPago = (t) => {
    const ultimo = ultimoPago(t.id);
    const p = PERIODOS.find(x => x.id === t.periodicidad);
    const inicio = ultimo && ultimo.hasta ? sumarDias(ultimo.hasta, 1) : hoy();
    setPagando({
      trabajador: t, ultimo,
      fecha: hoy(),
      desde: inicio,
      hasta: p && p.dias ? sumarDias(inicio, p.dias - 1) : hoy(),
      monto: t.sueldo || '',
      forma_pago: 'efectivo', nota: '',
    });
  };

  const guardarTrabajador = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = { ...editando, sueldo: parseFloat(editando.sueldo) || 0 };
      if (editando.id) await axios.put(`/api/trabajadores/${editando.id}`, datos);
      else await axios.post('/api/trabajadores', datos);
      setAviso({ tipo: 'bueno', texto: `${editando.nombre} quedó guardado` });
      setEditando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const guardarPago = async (e) => {
    e.preventDefault();
    const monto = parseFloat(pagando.monto);
    if (!(monto > 0)) return setAviso({ tipo: 'malo', texto: 'El pago tiene que ser mayor que cero' });
    setGuardando(true);
    try {
      await axios.post('/api/pagos-trabajadores', {
        trabajador_id: pagando.trabajador.id,
        fecha: pagando.fecha, desde: pagando.desde, hasta: pagando.hasta,
        monto, forma_pago: pagando.forma_pago, nota: pagando.nota || null,
      });
      setAviso({
        tipo: 'bueno',
        texto: `Pagado ${plata(monto)} a ${pagando.trabajador.nombre}`,
      });
      setPagando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar el pago' });
    }
    setGuardando(false);
  };

  // Aviso de periodo repetido: se compara contra lo ya pagado a esa persona.
  const seSolapa = pagando && pagos.some(p =>
    p.trabajador_id === pagando.trabajador.id && p.desde && p.hasta &&
    !(pagando.hasta < p.desde || pagando.desde > p.hasta));

  return (
    <>
      <div className="encabezado">
        <h1>Nómina</h1>
        <p>La gente que cobra por periodo, y lo que se le ha pagado</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--acero)' }}>
          <div className="rotulo">👷 Pagado en el periodo</div>
          <div className="valor cifra">{plata(totalPagado)}</div>
          <div className="pie">{enRango.length} {enRango.length === 1 ? 'pago' : 'pagos'}</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--ojo)' }}>
          <div className="rotulo">📅 Cuesta al mes</div>
          <div className="valor cifra">{plata(costoMensual)}</div>
          <div className="pie">lo pactado, se venda o no</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--bueno)' }}>
          <div className="rotulo">👥 Gente</div>
          <div className="valor cifra">{trabajadores.length}</div>
          <div className="pie">
            {porObra.length > 0 ? `${porObra.length} por trabajo` : 'todos por periodo'}
          </div>
        </div>
      </div>

      <div className="bloque">
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
        </div>
      </div>

      <div className="bloque">
        <h2>👷 La gente <span className="contador">{trabajadores.length}</span></h2>
        <div className="cuerpo">
          <div className="acciones" style={{ marginBottom: 14 }}>
            <button className="boton" onClick={() => setEditando({ ...VACIO })}>
              ➕ Agregar persona
            </button>
          </div>

          {trabajadores.length === 0 ? (
            <div className="vacio">
              Todavía no hay nadie. Agrega a quien te ayuda en el negocio y con qué
              frecuencia le pagas.
            </div>
          ) : (
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Cargo</th>
                    <th>Cada cuánto</th>
                    <th style={{ textAlign: 'right' }}>Pactado</th>
                    <th>Último pago</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {trabajadores.map(t => {
                    const u = ultimoPago(t.id);
                    const p = PERIODOS.find(x => x.id === t.periodicidad);
                    const obra = t.periodicidad === 'por_trabajo';
                    return (
                      <tr key={t.id}>
                        <td className="recio">
                          {t.nombre}
                          {t.telefono && (
                            <div className="cifra" style={{ fontSize: 11, fontWeight: 400,
                                                            color: 'var(--tinta-tenue)' }}>
                              📞 {t.telefono}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 13 }}>{t.cargo || '—'}</td>
                        <td>
                          <span className={'pinta ' + (obra ? 'obra' : 'acero')}>
                            {p ? p.nombre : t.periodicidad}
                          </span>
                        </td>
                        <td className="num">{t.sueldo > 0 ? plata(t.sueldo) : '—'}</td>
                        <td style={{ fontSize: 13 }}>
                          {u ? (
                            <>
                              {fecha(u.fecha)}
                              {u.hasta && (
                                <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                                  cubre hasta {fecha(u.hasta)}
                                </div>
                              )}
                            </>
                          ) : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {!obra && (
                            <>
                              <button className="boton chico" onClick={() => abrirPago(t)}>
                                💵 Pagar
                              </button>{' '}
                            </>
                          )}
                          <button className="boton chico suave"
                                  onClick={() => setEditando({ ...VACIO, ...t })}>
                            ✏️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {porObra.length > 0 && (
            <div className="aviso ojo" style={{ marginTop: 14 }}>
              A los que cobran <strong>por trabajo</strong> no se les paga desde aquí.
              Lo que les pagas se anota en la venta, en la línea de instalación, para
              que cada obra sepa lo que costó.
            </div>
          )}
        </div>
      </div>

      {/* ── Crear o editar persona ──────────────────────────────────────── */}
      {editando && (
        <div className="bloque">
          <h2>{editando.id ? '✏️ Editar' : '➕ Nueva persona'}</h2>
          <form onSubmit={guardarTrabajador}>
            <div className="cuerpo">
              <div className="campos">
                <label className="campo">
                  <span>Nombre *</span>
                  <input value={editando.nombre} required autoFocus
                         onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cédula</span>
                  <input value={editando.documento || ''}
                         onChange={e => setEditando({ ...editando, documento: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Teléfono</span>
                  <input value={editando.telefono || ''}
                         onChange={e => setEditando({ ...editando, telefono: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cargo</span>
                  <input value={editando.cargo || ''} placeholder="Ayudante, instalador…"
                         onChange={e => setEditando({ ...editando, cargo: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cada cuánto se le paga</span>
                  <select value={editando.periodicidad}
                          onChange={e => setEditando({ ...editando, periodicidad: e.target.value })}>
                    {PERIODOS.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </label>
                <label className="campo">
                  <span>Cuánto por periodo</span>
                  <input type="number" step="1" min="0" className="num-grande"
                         value={editando.sueldo} placeholder="0"
                         disabled={editando.periodicidad === 'por_trabajo'}
                         onChange={e => setEditando({ ...editando, sueldo: e.target.value })} />
                </label>
              </div>

              {editando.periodicidad === 'por_trabajo' && (
                <div className="aviso ojo" style={{ marginTop: 12 }}>
                  Quien cobra por trabajo no tiene sueldo fijo: lo que le pagues se
                  anota en cada venta con instalación.
                </div>
              )}

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setEditando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ── Registrar un pago ───────────────────────────────────────────── */}
      {pagando && (
        <div className="bloque">
          <h2>💵 Pagarle a {pagando.trabajador.nombre}</h2>
          <form onSubmit={guardarPago}>
            <div className="cuerpo">
              {pagando.ultimo ? (
                <div className="aviso" style={{ marginBottom: 16 }}>
                  El último pago fue el {fecha(pagando.ultimo.fecha)}
                  {pagando.ultimo.hasta && <> y cubrió hasta el {fecha(pagando.ultimo.hasta)}</>}.
                </div>
              ) : (
                <div className="aviso" style={{ marginBottom: 16 }}>
                  Es el primer pago que le registras.
                </div>
              )}

              <div className="campos">
                <label className="campo">
                  <span>Cuánto *</span>
                  <input type="number" step="1" min="0" className="num-grande" required autoFocus
                         value={pagando.monto} placeholder="0"
                         onChange={e => setPagando({ ...pagando, monto: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Fecha del pago</span>
                  <input type="date" value={pagando.fecha}
                         onChange={e => setPagando({ ...pagando, fecha: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Periodo desde</span>
                  <input type="date" value={pagando.desde}
                         onChange={e => setPagando({ ...pagando, desde: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Periodo hasta</span>
                  <input type="date" value={pagando.hasta}
                         onChange={e => setPagando({ ...pagando, hasta: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cómo le pagó</span>
                  <select value={pagando.forma_pago}
                          onChange={e => setPagando({ ...pagando, forma_pago: e.target.value })}>
                    <option value="efectivo">💵 Efectivo</option>
                    <option value="transferencia">🏦 Transferencia</option>
                  </select>
                </label>
              </div>

              {seSolapa && (
                <div className="aviso malo" style={{ marginTop: 14 }}>
                  <strong>Ojo:</strong> ya hay un pago registrado que cubre parte de esas
                  mismas fechas. Comprueba que no se le esté pagando dos veces el mismo
                  periodo.
                </div>
              )}

              <label className="campo" style={{ marginTop: 12 }}>
                <span>Nota</span>
                <input value={pagando.nota} placeholder="Lo que convenga recordar"
                       onChange={e => setPagando({ ...pagando, nota: e.target.value })} />
              </label>

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setPagando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Registrar pago'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="bloque">
        <h2>📋 Pagos del periodo <span className="contador">{enRango.length}</span></h2>
        {enRango.length === 0 ? (
          <div className="vacio">No hay pagos de nómina en estas fechas.</div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>A quién</th>
                  <th>Periodo que cubre</th>
                  <th>Pago</th>
                  <th style={{ textAlign: 'right' }}>Monto</th>
                </tr>
              </thead>
              <tbody>
                {enRango.map(p => {
                  const t = trabajadores.find(x => x.id === p.trabajador_id);
                  return (
                    <tr key={p.id}>
                      <td>{fecha(p.fecha)}</td>
                      <td className="recio">{t ? t.nombre : '—'}</td>
                      <td style={{ fontSize: 13 }}>
                        {p.desde && p.hasta
                          ? `${fecha(p.desde)} → ${fecha(p.hasta)}`
                          : '—'}
                      </td>
                      <td>
                        <span className={'pinta ' + (p.forma_pago === 'efectivo' ? 'bueno' : 'acero')}>
                          {p.forma_pago}
                        </span>
                      </td>
                      <td className="num recio">{plata(p.monto)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default Nomina;
